import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Eye, 
  CheckCircle, 
  XCircle, 
  Clock, 
  Calendar,
  Download,
  Search,
  Lock,
  Globe,
  User,
} from 'lucide-react';
import { Button } from '../../components/shared';
import DataTable from '../../components/shared/DataTable';
import DatePicker from '../../components/client/DatePicker';
import { useAdminBookings, useAdminBookingStatistics, useExportAdminBookings } from '../../services/adminBookingService';
import { formatDate, formatTime } from '../../utils/dateUtils';

const SOURCE_TABS = [
  { key: 'platform', label: 'Platform Bookings', icon: Globe, activeClass: 'border-gray-900 text-gray-900' },
  { key: 'private', label: 'Private Bookings', icon: Lock, activeClass: 'border-violet-600 text-violet-700' },
];

const isPrivateBooking = (booking) => booking.booking_source === 'vendor_manual';

const getClientDisplayName = (booking) => {
  if (isPrivateBooking(booking)) {
    return booking.guest_name || booking.manual_title || 'Private client';
  }
  return booking.client_display_name || booking.client?.name || 'Unknown Client';
};

const AdminBookings = () => {
  const navigate = useNavigate();
  const [sourceTab, setSourceTab] = useState('platform');
  const [filters, setFilters] = useState({
    status: '',
    search: '',
    date_from: '',
    date_to: ''
  });

  const isPrivateTab = sourceTab === 'private';

  const cleanFilters = Object.fromEntries(
    Object.entries(filters).filter(([_, value]) => value !== '' && value !== null && value !== undefined)
  );

  const apiFilters = {
    ...cleanFilters,
    booking_source: isPrivateTab ? 'vendor_manual' : 'platform',
    per_page: 50,
    ...(isPrivateTab && filters.search ? { guest_search: filters.search } : {}),
    ...(!isPrivateTab && filters.search ? { client_search: filters.search } : {}),
  };
  delete apiFilters.search;

  const { data: bookingsResponse, isLoading, error } = useAdminBookings(apiFilters);
  const { data: statsResponse } = useAdminBookingStatistics();

  const stats = bookingsResponse?.statistics || statsResponse?.data || {};
  const tabTotal = bookingsResponse?.data?.total ?? 0;

  const allBookings = bookingsResponse?.data?.data || [];
  const bookings = allBookings.filter((booking) => booking.status !== 'pending');

  const exportBookingsMutation = useExportAdminBookings({
    onSuccess: () => {
      console.log('Bookings exported successfully');
    },
    onError: (exportError) => {
      console.error('Failed to export bookings:', exportError);
    }
  });

  const getStatusConfig = (status) => {
    const statusConfig = {
      pending: { color: 'bg-yellow-100 text-yellow-800', icon: Clock, label: 'Pending' },
      confirmed: { color: 'bg-indigo-100 text-indigo-800', icon: CheckCircle, label: 'Confirmed' },
      in_progress: { color: 'bg-blue-100 text-blue-800', icon: Clock, label: 'In Progress' },
      completed: { color: 'bg-green-100 text-green-800', icon: CheckCircle, label: 'Completed' },
      cancelled: { color: 'bg-red-100 text-red-800', icon: XCircle, label: 'Cancelled' },
      no_show: { color: 'bg-gray-100 text-gray-800', icon: XCircle, label: 'No Show' }
    };
    return statusConfig[status] || statusConfig.pending;
  };

  const truncateText = (text, maxLength = 12) => {
    if (!text) return 'N/A';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const columns = useMemo(() => [
    {
      key: 'booking_reference',
      label: 'Booking #',
      sortable: true,
      width: 'w-32',
      render: (booking) => (
        <div className="flex items-center gap-1.5 min-w-0">
          <span
            className={`font-mono text-xs font-medium truncate block ${isPrivateTab ? 'text-violet-700' : 'text-primary-600'}`}
            title={`#${booking.booking_reference}`}
          >
            #{truncateText(booking.booking_reference, 12)}
          </span>
          {isPrivateTab && (
            <span className="inline-flex items-center gap-0.5 px-1 py-0.5 rounded text-[9px] font-semibold uppercase bg-violet-100 text-violet-700 border border-violet-200 flex-shrink-0">
              <Lock className="w-2 h-2" />
            </span>
          )}
        </div>
      )
    },
    {
      key: 'vendor',
      label: 'Vendor',
      sortable: true,
      width: 'w-48',
      render: (booking) => (
        <div className="min-w-0">
          <p className="text-sm font-medium text-gray-900 truncate">
            {booking.vendor?.business_name || 'Unknown Vendor'}
          </p>
          <p className="text-xs text-gray-500 truncate mt-0.5">
            {booking.custom_service_name || booking.vendor_service?.service_name || 'Unknown Service'}
          </p>
        </div>
      )
    },
    {
      key: 'client',
      label: isPrivateTab ? 'Guest client' : 'Client',
      sortable: true,
      width: 'w-48',
      render: (booking) => {
        const isPrivate = isPrivateTab || isPrivateBooking(booking);
        const displayName = getClientDisplayName(booking);
        const subline = isPrivate
          ? booking.guest_email || booking.guest_phone || 'Vendor private booking'
          : booking.client?.email;

        return (
          <div className="flex items-center gap-2 min-w-0">
            <div
              className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${
                isPrivate ? 'bg-violet-100' : 'bg-gray-100'
              }`}
            >
              {isPrivate ? (
                <Lock className="w-3.5 h-3.5 text-violet-600" />
              ) : (
                <User className="w-3.5 h-3.5 text-gray-600" />
              )}
            </div>
            <div className="min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">{displayName}</p>
              {subline && (
                <p className={`text-xs truncate mt-0.5 ${isPrivate ? 'text-violet-600/80' : 'text-gray-500'}`}>
                  {subline}
                </p>
              )}
            </div>
          </div>
        );
      }
    },
    {
      key: 'event_date',
      label: 'Date',
      sortable: true,
      width: 'w-32',
      render: (booking) => (
        <div className="flex items-center space-x-2">
          <Calendar className="w-4 h-4 text-gray-400 flex-shrink-0" />
          <div className="min-w-0">
            <div className="text-sm font-medium text-gray-900">
              {formatDate(booking.event_date, 'medium')}
            </div>
            {booking.start_time && (
              <div className="text-xs text-gray-500">
                {formatTime(booking.start_time)}
              </div>
            )}
          </div>
        </div>
      )
    },
    {
      key: 'total_amount',
      label: 'Amount',
      sortable: true,
      width: 'w-24',
      render: (booking) => {
        const formatCurrency = (value) => {
          const num = parseFloat(value);
          if (isNaN(num) || num === null || num === undefined) {
            return '0.00';
          }
          return num.toFixed(2);
        };

        const subtotal = parseFloat(booking.subtotal_amount || 0);
        const platformFee = parseFloat(booking.platform_fee || 0);
        const totalAmountDue = subtotal + platformFee;

        const completedPayments = booking.payments?.filter(p => p.status === 'completed') || [];
        const totalAmountPaid = completedPayments.reduce((sum, payment) => {
          return sum + parseFloat(payment.amount || 0);
        }, 0);

        const depositAmountPaid = completedPayments
          .filter(p => p.payment_type === 'deposit')
          .reduce((sum, payment) => sum + parseFloat(payment.amount || 0), 0);

        const hasDepositPayment = completedPayments.some(p => p.payment_type === 'deposit');
        const hasFullPayment = completedPayments.some(p => p.payment_type === 'full_payment');
        const isFullyPaid = totalAmountPaid >= totalAmountDue && totalAmountDue > 0;

        const showDepositAmount = hasDepositPayment && !hasFullPayment && !isFullyPaid;
        const amountToDisplay = showDepositAmount
          ? depositAmountPaid
          : (completedPayments.length > 0
              ? totalAmountPaid
              : (isPrivateTab ? subtotal : totalAmountDue));

        return (
          <div className="text-right">
            <p className="text-sm font-medium text-gray-900">
              ${formatCurrency(amountToDisplay)}
            </p>
            {showDepositAmount && (
              <p className="text-xs text-gray-500 mt-0.5">Deposit</p>
            )}
            {!showDepositAmount && completedPayments.length > 0 && !isFullyPaid && (
              <p className="text-xs text-gray-500 mt-0.5">
                of ${formatCurrency(totalAmountDue)}
              </p>
            )}
          </div>
        );
      }
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      width: 'w-28',
      render: (booking) => {
        const statusConfig = getStatusConfig(booking.status);
        const StatusIcon = statusConfig.icon;
        return (
          <span className="whitespace-nowrap flex-shrink-0">
            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${statusConfig.color}`}>
              <StatusIcon className="w-3 h-3 mr-1" />
              {statusConfig.label}
            </span>
          </span>
        );
      }
    }
  ], [isPrivateTab]);

  const actions = [
    {
      label: 'View Details',
      icon: Eye,
      variant: 'default',
      onClick: (booking) => {
        navigate(`/admin/bookings/${booking.id}`);
      }
    }
  ];

  const handleSearch = (data, searchTerm) => {
    if (!searchTerm) return data;

    return data.filter(booking =>
      booking.booking_reference?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.vendor?.business_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.client?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.guest_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.guest_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.manual_title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.vendor_service?.service_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.custom_service_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.event_location?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const clearAllFilters = () => {
    setFilters({
      status: '',
      search: '',
      date_from: '',
      date_to: ''
    });
  };

  const handleExport = () => {
    exportBookingsMutation.mutate(apiFilters);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Bookings</h3>
          <p className="text-gray-600">Unable to load bookings data.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="p-6">
        <div className="bg-white rounded-lg border border-gray-200 p-6 mb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-purple-500 rounded-lg flex items-center justify-center">
                <Calendar className="w-4 h-4 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">All Bookings</h1>
                <p className="text-gray-600">Manage platform and vendor private bookings</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <Button
                variant="outline"
                icon={Download}
                onClick={handleExport}
                disabled={exportBookingsMutation.isPending}
              >
                {exportBookingsMutation.isPending ? 'Exporting...' : 'Export'}
              </Button>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-t-lg border border-gray-200 border-b-0 px-4 pt-4">
          <nav className="flex flex-wrap gap-1" aria-label="Booking source tabs">
            {SOURCE_TABS.map((tab) => {
              const Icon = tab.icon;
              const isActive = sourceTab === tab.key;
              return (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setSourceTab(tab.key)}
                  className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-t-lg text-sm font-medium border-b-2 -mb-px transition-colors ${
                    isActive
                      ? `${tab.activeClass} bg-white`
                      : 'border-transparent text-gray-600 hover:bg-gray-50 hover:text-gray-800'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                  {isActive && (
                    <span className="text-xs text-gray-400 font-normal">({tabTotal})</span>
                  )}
                </button>
              );
            })}
          </nav>
        </div>

        <div className="bg-white rounded-b-lg border border-gray-200 p-6 mb-4">
          <div className="flex flex-wrap gap-2 border-b border-gray-200 pb-4 mb-4">
            {[
              { value: '', label: 'All', count: stats.total_bookings || 0 },
              { value: 'pending', label: 'Pending', count: stats.pending_bookings || 0 },
              { value: 'confirmed', label: 'Confirmed', count: stats.confirmed_bookings || 0 },
              { value: 'in_progress', label: 'In Progress', count: stats.in_progress_bookings || 0 },
              { value: 'completed', label: 'Completed', count: stats.completed_bookings || 0 },
              { value: 'cancelled', label: 'Cancelled', count: stats.cancelled_bookings || 0 },
              { value: 'no_show', label: 'No Show', count: stats.no_show_bookings || 0 }
            ].map((tab) => (
              <button
                key={tab.value || 'all'}
                onClick={() => handleFilterChange('status', tab.value)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  filters.status === tab.value
                    ? 'bg-gray-900 text-white'
                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                }`}
              >
                {tab.label} ({tab.count})
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {isPrivateTab ? 'Search guest' : 'Search client'}
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  placeholder={isPrivateTab ? 'Guest name, email, title...' : 'Client name or email...'}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>

            <div>
              <DatePicker
                label="From Date"
                value={filters.date_from}
                onChange={(value) => handleFilterChange('date_from', value)}
                placeholder="Select start date"
                allowPastDates={true}
                className="w-full"
                size="md"
              />
            </div>

            <div>
              <DatePicker
                label="To Date"
                value={filters.date_to}
                onChange={(value) => handleFilterChange('date_to', value)}
                placeholder="Select end date"
                allowPastDates={true}
                minDate={filters.date_from || null}
                className="w-full"
                size="md"
              />
            </div>
          </div>

          <div className="flex justify-end mt-4">
            <Button variant="outline" onClick={clearAllFilters}>
              Clear Filters
            </Button>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200">
          <DataTable
            data={bookings}
            columns={columns}
            actions={actions}
            onSearch={handleSearch}
            searchPlaceholder={
              isPrivateTab
                ? 'Search private bookings...'
                : 'Search platform bookings...'
            }
            emptyMessage={
              isPrivateTab
                ? 'No private bookings found'
                : 'No platform bookings found'
            }
            loading={isLoading}
          />
        </div>
      </div>
    </div>
  );
};

export default AdminBookings;
